// In Next.js, this file would be called: app/providers.jsx
"use client"

import { ThemeProvider } from "@/components/theme-provider"
import { Toaster } from "sonner"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

// We can not useState or useRef in a server component, which is why we are
// extracting this part out into it's own file with 'use client' on top
import { PropsWithChildren, useEffect, useState } from "react"
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query"
import { baseUrl, defaultQueryFn } from "@/api/default-query"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AppProgressBar as ProgressBar } from "next-nprogress-bar"
import { Provider } from "jotai"
import { NuqsAdapter } from "nuqs/adapters/next/app"
import { UpgradeSubscription } from "@/components/upgrade-subscription"

import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { useLogout } from "@/api"
import { ScreenSizeContext } from "@/components/screen.size.context"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useRouter } from "next/navigation"

const isLocalBuild = process.env.NEXT_PUBLIC_AUTH_METHOD === "local"
const isPublicPath = (host: string, pathname: string) => {
  return pathname.startsWith("/trygenie") || pathname.startsWith("/demo") || host === "trygenie.now"
}

if (typeof window !== "undefined" && !isLocalBuild) {
  const POSTHOG_URL_INTERCEPT = "/935b"
  const POSTHOG_HOST = `${process.env.ENVIRONMENT === "prod" ? "https://cosine.sh" : "https://cosine.wtf"}${POSTHOG_URL_INTERCEPT}`
  posthog.init(
    process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "phc_pAfRkta2U5zhpNjqNevjnonN5TbssAy5lrybpySWFRL",
    {
      api_host: "/ingest",
      ui_host: POSTHOG_HOST,
    },
  )
}

export function CSPostHogProvider({ children }: { children: React.ReactNode }) {
  return !isLocalBuild ? (
    <PostHogProvider client={posthog}>{children}</PostHogProvider>
  ) : (
    <>{children}</>
  )
}

function SessionRefresher() {
  const logoutMutation = useLogout()

  useEffect(() => {
    if (!isLocalBuild) {
      const validateSession = async () => {
        try {
          const response = await fetch(`${baseUrl}/users/refreshSession`, {
            method: "GET",
            credentials: "include",
          })
          if (response.status === 401) {
            const logoutMutation = useLogout()
            logoutMutation.mutate()
          }
        } catch (e) {
          logoutMutation.mutate()
        }
      }

      validateSession()
    }
  }, [])

  return null
}

export default function Providers({ children }: PropsWithChildren<object>) {
  const isMobile = useMediaQuery("(max-width: 768px)")
  const router = useRouter()
  const host = typeof window !== "undefined" ? window.location.host : ""
  const pathname = typeof window !== "undefined" ? window.location.pathname : ""

  // Global error handler for React Query
  function handleUnauthorized(error: any) {
    // Check for 403 error code on error.cause.status
    if (error?.cause?.status === 403 && !isPublicPath(host, pathname)) {
      const searchParams = new URLSearchParams(window.location.search)
      // only add return to if it is not already present, and make sure we never say to return to the unauthorized page
      const redirectUrl =
        searchParams.has("returnTo") || pathname === "/unauthorized"
          ? `/unauthorized` + window.location.search
          : `/unauthorized?returnTo=${encodeURIComponent(pathname)}` + window.location.search
      router.push(redirectUrl)
    }
  }

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000 /** 1minute */ * 5,
            queryFn: defaultQueryFn,
            retry: false,
          },
        },
        queryCache: new QueryCache({
          onError: handleUnauthorized,
        }),
        mutationCache: new MutationCache({
          onError: handleUnauthorized,
        }),
      }),
  )

  return (
    <Provider>
      <ScreenSizeContext.Provider value={{ isMobile }}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" enableSystem>
            <NuqsAdapter>
              <TooltipProvider delayDuration={400}>
                <SessionRefresher />
                {children}
              </TooltipProvider>
            </NuqsAdapter>
            <Toaster richColors />
            <UpgradeSubscription />
          </ThemeProvider>
          <ProgressBar
            height="4px"
            color="#FBABFF"
            options={{ showSpinner: false }}
            shallowRouting
          />
          {!process.env.NEXT_PUBLIC_DISABLE_DEV_TOOLS && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </ScreenSizeContext.Provider>
    </Provider>
  )
}
